/**
 * Table
 *
 * Generic paginated table component used by /jobs, /cost, and /health-checks.
 * No virtual scrolling per CONSTITUTION — tables are tables.
 */

import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: ReactNode;
  render: (row: T, index: number) => ReactNode;
  width?: string;
  sortable?: boolean;
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  keyFn: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  emptyMessage?: ReactNode;
  /** Pagination */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  /** Sorting */
  sortBy?: string;
  sortDesc?: boolean;
  onSort?: (key: string, desc: boolean) => void;
}

export function Table<T>({
  columns,
  rows,
  keyFn,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No data.',
  page = 1,
  pageSize = 50,
  total,
  onPageChange,
  sortBy,
  sortDesc,
  onSort,
}: TableProps<T>) {
  const totalPages = total !== undefined ? Math.ceil(total / pageSize) : undefined;

  function handleSort(colKey: string) {
    if (!onSort) return;
    if (sortBy === colKey) {
      onSort(colKey, !sortDesc);
    } else {
      onSort(colKey, false);
    }
  }

  return (
    <div className="flex flex-col gap-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead>
            <tr className="border-b border-zinc-800">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={[
                    'px-3 py-2 text-xs font-medium text-zinc-400 uppercase tracking-wider whitespace-nowrap',
                    col.width ? col.width : '',
                    col.sortable && onSort ? 'cursor-pointer hover:text-zinc-200 select-none' : '',
                  ].join(' ')}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  aria-sort={
                    sortBy === col.key
                      ? sortDesc
                        ? 'descending'
                        : 'ascending'
                      : col.sortable
                      ? 'none'
                      : undefined
                  }
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.sortable && sortBy === col.key && (
                      <span aria-hidden="true" className="text-violet-400">
                        {sortDesc ? '▼' : '▲'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="border-b border-zinc-800/50">
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-3">
                      <div className="h-4 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-12 text-center text-zinc-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={keyFn(row, index)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={[
                    'border-b border-zinc-800/50 hover:bg-zinc-800/40 transition-colors',
                    onRowClick ? 'cursor-pointer' : '',
                  ].join(' ')}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2 text-zinc-200">
                      {col.render(row, index)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages !== undefined && totalPages > 1 && onPageChange && (
        <div className="flex items-center justify-between px-3 py-3 border-t border-zinc-800">
          <span className="text-xs text-zinc-500">
            Page {page} of {totalPages}
            {total !== undefined && ` — ${total.toLocaleString()} total`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="px-3 py-1 text-xs rounded bg-zinc-800 text-zinc-300 disabled:opacity-40 hover:bg-zinc-700 transition-colors min-h-[48px]"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="px-3 py-1 text-xs rounded bg-zinc-800 text-zinc-300 disabled:opacity-40 hover:bg-zinc-700 transition-colors min-h-[48px]"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
